import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Car,
  LayoutDashboard,
  Wallet,
  LogOut,
  FileText,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Zap,
  Grid2X2,
  Users,
  ShieldAlert,
  ChevronDown,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useApp } from '@/store/AppContext'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/use-auth'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'

const navItems = [
  {
    icon: ShieldAlert,
    label: 'Painel Master / Gestão',
    path: '/management',
    roles: ['ultramaster', 'admin'],
  },
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    path: '/dashboard',
    roles: ['admin', 'operator', 'ultramaster'],
  },
  {
    icon: Car,
    label: 'Check-out',
    path: '/checkout',
    roles: ['admin', 'operator', 'ultramaster'],
  },
  {
    icon: Grid2X2,
    label: 'Mapa de Vagas',
    path: '/map',
    roles: ['admin', 'operator', 'ultramaster'],
  },
  {
    icon: Wallet,
    label: 'Caixa',
    path: '/cashier',
    roles: ['admin', 'operator', 'ultramaster'],
  },
  {
    icon: Users,
    label: 'Mensalistas',
    path: '/subscribers',
    roles: ['admin', 'operator', 'ultramaster'],
  },
  {
    icon: FileText,
    label: 'Relatórios Avançados',
    path: '/reports',
    roles: ['admin', 'ultramaster', 'financial'],
  },
  {
    icon: SlidersHorizontal,
    label: 'Regras de Preço',
    path: '/pricing',
    roles: ['admin', 'ultramaster'],
  },
  {
    icon: SettingsIcon,
    label: 'Configurações',
    path: '/settings',
    roles: ['admin', 'ultramaster'],
  },
]

export function Sidebar({ className }: { className?: string }) {
  const location = useLocation()
  const { currentSession, isOperationMode, toggleOperationMode } = useApp()
  const { profile, userLots, switchLot, signOut } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const activeLot = userLots.find(
    (l) => l.parking_lot_id === profile?.parking_lot_id,
  )?.parking_lots

  return (
    <aside className={cn('flex flex-col border-r bg-card h-full', className)}>
      <div
        className="p-6 flex items-center gap-3 border-b"
        data-tour="tour-lot-selector"
      >
        <div className="bg-primary text-primary-foreground p-2 rounded-lg flex-shrink-0 shadow-sm">
          <Car className="h-6 w-6" />
        </div>
        <div className="overflow-hidden w-full">
          <h1 className="font-bold text-xl leading-tight truncate tracking-tight">
            {profile?.role === 'ultramaster'
              ? 'UltraMaster'
              : activeLot?.name || 'Selecione'}
          </h1>
          <p className="text-xs text-muted-foreground truncate">
            {profile?.role === 'ultramaster'
              ? 'Gestão Global'
              : activeLot?.address || 'Sem endereço'}
          </p>
        </div>

        {userLots.length > 1 && profile?.role !== 'ultramaster' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-8 w-8 ml-auto shrink-0 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {userLots.map((lot) => (
                <DropdownMenuItem
                  key={lot.parking_lot_id}
                  onClick={() => switchLot(lot.parking_lot_id)}
                >
                  {lot.parking_lots.name}
                  {profile.parking_lot_id === lot.parking_lot_id && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="p-4 border-b space-y-3">
        <div
          className={cn(
            'px-3 py-2 rounded-md text-sm font-medium flex items-center justify-between shadow-sm',
            currentSession?.status === 'open'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800',
          )}
        >
          <span>
            Caixa {currentSession?.status === 'open' ? 'Aberto' : 'Fechado'}
          </span>
          <span className="flex h-2 w-2 rounded-full bg-current shadow-[0_0_8px_currentColor]"></span>
        </div>
        <div className="flex items-center justify-between px-2 text-xs text-muted-foreground">
          <span>Perfil:</span>
          <span className="font-bold capitalize">
            {profile?.role || 'Visitante'}
          </span>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {profile?.role !== 'financial' && (
          <div className="flex items-center justify-between px-3 py-3 rounded-md border bg-yellow-50 text-yellow-800 mb-4">
            <div className="flex items-center gap-2 font-bold text-sm">
              <Zap className="h-4 w-4 fill-yellow-500 text-yellow-500" /> Modo
              Operação
            </div>
            <Switch
              checked={isOperationMode}
              onCheckedChange={toggleOperationMode}
            />
          </div>
        )}

        {navItems
          .filter((item) => profile && item.roles.includes(profile.role))
          .map((item) => {
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                data-tour={`tour-nav-${item.path.slice(1)}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {item.label}
              </Link>
            )
          })}
      </nav>

      <div className="p-4 border-t mt-auto">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive w-full transition-colors"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          Sair do Sistema
        </button>
      </div>
    </aside>
  )
}
